import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Loader2, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { IMG, MINISTRY } from "@/lib/content";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in your name, email, and message."); return; }
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message || "Message sent!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const info = [
    { icon: MapPin, label: "Visit", value: MINISTRY.address },
    { icon: Phone, label: "Call", value: MINISTRY.phone, href: `tel:${MINISTRY.phone}` },
    { icon: Mail, label: "Email", value: MINISTRY.email, href: `mailto:${MINISTRY.email}` },
    { icon: Clock, label: "Gatherings", value: MINISTRY.serviceTimes },
  ];

  return (
    <>
      <PageHero testid="contact-hero" kicker="Contact" title="We'd love to hear from you" subtitle="Questions, partnership ideas, or a need we can pray over — reach out and let's connect." image={IMG.group} />

      <section className="py-20 sm:py-28 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
          {/* Info + form */}
          <div className="lg:col-span-3">
            <Reveal>
              <h2 className="font-heading text-3xl sm:text-4xl text-plum font-semibold">Send us a message</h2>
              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-plum text-sm">Full name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="h-12 bg-white border-plum/15 mt-1" /></div>
                  <div><Label className="text-plum text-sm">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="h-12 bg-white border-plum/15 mt-1" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-plum text-sm">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" className="h-12 bg-white border-plum/15 mt-1" /></div>
                  <div><Label className="text-plum text-sm">Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" className="h-12 bg-white border-plum/15 mt-1" /></div>
                </div>
                <div><Label className="text-plum text-sm">Message *</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" rows={6} className="bg-white border-plum/15 mt-1" /></div>
                <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="bg-plum hover:bg-plum-dark text-cream font-semibold rounded-full px-8 py-4 flex items-center gap-2 transition-colors disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}{loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </Reveal>
          </div>

          {/* Contact cards */}
          <div className="lg:col-span-2">
            <Reveal delay={0.1}>
              <div className="bg-plum rounded-2xl p-8 text-cream space-y-6">
                {info.map((it) => (
                  <div key={it.label} className="flex gap-4" data-testid={`contact-info-${it.label.toLowerCase()}`}>
                    <span className="grid place-items-center w-11 h-11 rounded-xl bg-cream/10 text-gold shrink-0"><it.icon size={20} /></span>
                    <div>
                      <p className="text-gold-light text-xs uppercase tracking-wider font-semibold">{it.label}</p>
                      {it.href ? <a href={it.href} className="text-cream hover:text-gold transition-colors whitespace-nowrap">{it.value}</a> : <p className="text-cream/90 text-sm leading-relaxed">{it.value}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        {/* MAP */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <Reveal>
            <div className="rounded-2xl overflow-hidden shadow-xl border border-plum/10" data-testid="contact-map">
              <iframe
                title="Greater Faith International Ministries location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(MINISTRY.mapQuery)}&z=15&output=embed`}
                width="100%" height="440" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
